import * as Sentry from '@sentry/react'
import React, { type PropsWithChildren, useRef } from 'react'
import { Overlay, Popover } from 'react-bootstrap'
import { Menu, MenuItem, type MenuItemStyles } from 'react-pro-sidebar'
import { NavLink, useLocation } from 'react-router-dom'
import classNames from 'classnames'

import { AdvancedModeSwitch } from '../../components/AdvancedModeSwitch/AdvancedModeSwitch'
import { useZocksAuth, useZocksQuota } from '../../components/ScopedBlock/hooks'
import { Divider } from '../../components/Divider/Divider'
import { ScopedBlock } from '../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction, type ScopeDescriptor } from '../../components/ScopedBlock/models'

import { useAccount } from '../../common/useAccount'
import { useAppDispatch } from '../../store/hooks'
import { addToast } from '../../store/toast/toastSlice'
import { useLoginToAcademyMutation } from '../../store/auth/auth'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'

import { EnterpriseDashboardMenu } from './EnterpriseDashboardMenu'
import { useTrackedMenuItem } from './useTrackedMenuItem'
import { useHoverPopover } from './useHoverPopover'
import { SETTINGS_ENTRIES, isSettingsRouteActive } from './settingsEntries'

import './SidebarFooter.scss'

type ActivePopover = 'settings' | 'help'

export interface SidebarFooterProps {
  collapsed: boolean
  menuItemStyles: MenuItemStyles
  onItemClick?: () => void
  isMobile?: boolean
  onPopoverOpenChange?: (open: boolean) => void
}

// Only one popover is mounted at a time, so both triggers can point at the same id
const POPOVER_ID = 'sidebar-footer-popover'
const HELP_CENTER_URL = 'https://help.zocks.io'
const POPOVER_ENTRY_CLASS = 'sidebar-popover-entry d-flex align-items-center gap-2 px-2 py-1 rounded-1 w-100'

const popoverLinkClass = ({ isActive }: { isActive: boolean }) => classNames(POPOVER_ENTRY_CLASS, { 'sidebar-popover-entry--active': isActive })

// Passing `noopener` in the features string makes window.open return null, and we need the handle to
// navigate the tab once the URL arrives — so the opener is severed by hand instead
const openBlankTab = () => {
  const tab = window.open('', '_blank')
  if (tab) {
    tab.opener = null
  }
  return tab
}

const ScopeGate: React.FC<PropsWithChildren<{ scope?: ScopeDescriptor }>> = ({ scope, children }) =>
  scope ? (
    <ScopedBlock scope={scope} missingScopeAction={MissingScopeAction.HIDE}>
      {children}
    </ScopedBlock>
  ) : (
    <>{children}</>
  )

// Holds the mortarboard and the spinner at one size so swapping them cannot reflow the row
const AcademyIcon: React.FC<{ loading: boolean }> = ({ loading }) => (
  <span className='sidebar-icon-slot d-inline-flex align-items-center justify-content-center flex-shrink-0'>
    {loading ? <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' /> : <i className='bi bi-mortarboard' />}
  </span>
)

interface AcademyEntryProps {
  loading: boolean
  onClick: () => void
  variant: 'menu' | 'popover'
}

const AcademyEntry: React.FC<AcademyEntryProps> = ({ loading, onClick, variant }) => {
  const label = loading ? 'Signing in...' : 'Zocks Academy'

  return variant === 'popover' ? (
    <button
      type='button'
      className={POPOVER_ENTRY_CLASS}
      // Opts out of the click-to-close so the signing-in state stays visible until the tab opens
      data-keep-open
      onClick={loading ? undefined : onClick}
      disabled={loading}
      aria-busy={loading}
    >
      <AcademyIcon loading={loading} />
      <span className='button-small'>{label}</span>
    </button>
  ) : (
    <MenuItem
      icon={<AcademyIcon loading={loading} />}
      component={<button type='button' className='w-100 sidebar-trigger-button text-start' disabled={loading} aria-busy={loading} />}
      onClick={loading ? undefined : onClick}
    >
      <div className='button-small'>{label}</div>
    </MenuItem>
  )
}

export const SidebarFooter: React.FC<SidebarFooterProps> = ({ collapsed, menuItemStyles, onItemClick, isMobile = false, onPopoverOpenChange }) => {
  const { hasQuota } = useZocksQuota()
  const { hasScope } = useZocksAuth()
  const { account } = useAccount()
  const { trackingClient } = useTracking()
  const dispatch = useAppDispatch()
  const handleMenuItemClick = useTrackedMenuItem(onItemClick)
  const [loginToAcademy, loginToAcademyMutation] = useLoginToAcademyMutation()
  const loading = loginToAcademyMutation.isLoading

  const enterpriseDashboardAvailable = hasQuota('app.enterpriseDashboard.value')
  const hasSubAccountAdminScope = hasScope('**', 'sub_account:admin', ['sub_account:admin'])
  const hasDashboardListScope = hasScope('**', 'dashboard:list', ['dashboard:admin'])
  const hasDownstreamAccess = !!account?.effectiveConfig?.downstreamDashboards || !!account?.effectiveConfig?.downstreamInsights
  const isEnterpriseDashboardEnabled = (enterpriseDashboardAvailable && hasSubAccountAdminScope) || (hasDownstreamAccess && hasDashboardListScope)

  const { pathname } = useLocation()
  const isSettingsChildActive = isSettingsRouteActive(pathname)

  const settingsRef = useRef<HTMLDivElement>(null)
  const helpRef = useRef<HTMLDivElement>(null)
  const popoverBodyRef = useRef<HTMLDivElement>(null)
  // The academy login holds the popover open so its progress stays visible until the tab opens
  const popover = useHoverPopover<ActivePopover>('settings', { holdOpen: loading, onOpenChange: onPopoverOpenChange })

  // The tab is opened synchronously inside the click gesture: once the login request has been awaited
  // browsers no longer treat the popup as user-initiated and block it, so the placeholder is navigated
  // when the URL arrives and discarded if it never does
  const handleAcademyClick = async () => {
    trackingClient.track({ eventName: TrackEvent.ZOCKS_ACADEMY_CLICKED })
    const academyTab = openBlankTab()
    if (!academyTab) {
      // Popups are blocked, and opening one after the request would be blocked for the same reason
      dispatch(
        addToast({
          variant: 'error',
          title: 'Could not open Zocks Academy',
          messages: ['Allow pop-ups for this site and try again.'],
        }),
      )
      onItemClick?.()
      return
    }

    let loginUrl: string | undefined
    try {
      loginUrl = (await loginToAcademy(null).unwrap())?.data?.loginUrl
    } catch {
      // Failed API calls are tracked elsewhere
    }

    try {
      if (loginUrl) {
        // replace() keeps the blank placeholder out of the new tab's history
        academyTab.location.replace(loginUrl)
      } else {
        academyTab.close()
      }
    } catch (error) {
      Sentry.captureException(error)
      academyTab.close()
    }
    onItemClick?.()
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      const ref = (popover.activeItem ?? popover.lastItem) === 'settings' ? settingsRef : helpRef
      popover.closeNow()
      // react-pro-sidebar owns the ref it passes to the trigger, so focus is restored by looking
      // the button up through the class this file puts on it rather than the library's own
      ref.current?.querySelector<HTMLElement>('.sidebar-trigger-button')?.focus()
    }
  }

  const onPopoverItemClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement
    // Opted-out items stay mounted so their own handler runs and their loading state is visible
    if (target.closest('[data-keep-open]')) {
      return
    }
    if (target.closest('a, button')) {
      popover.closeNow()
    }
  }

  const onBlur = (e: React.FocusEvent) => {
    if (popover.activeItem === null) {
      return
    }
    const next = e.relatedTarget as Node | null
    if (next && (settingsRef.current?.contains(next) || helpRef.current?.contains(next) || popoverBodyRef.current?.contains(next))) {
      return
    }
    popover.scheduleClose()
  }

  const onTriggerClick = (item: ActivePopover) => (e: React.MouseEvent) => {
    // Clicking an already-open trigger keeps it open — open() also cancels any pending close
    popover.open(item)
    if (e.detail === 0) {
      requestAnimationFrame(() => popoverBodyRef.current?.querySelector<HTMLElement>('a, button')?.focus())
    }
  }

  // A keyboard user can be inside the popover while the pointer is somewhere else entirely, and
  // unmounting a focused element drops focus to the document. Leaving is only a close signal for
  // the pointer, so it defers to focus; tabbing out or Escape closes the popover from there.
  const onMouseLeave = () => {
    if (popoverBodyRef.current?.contains(document.activeElement)) {
      return
    }
    popover.scheduleClose()
  }

  const triggerHandlers = (item: ActivePopover) => ({
    onMouseEnter: () => popover.open(item),
    onMouseLeave,
    onKeyDown,
    onBlur,
  })

  const targetRef = popover.lastItem === 'help' ? helpRef : settingsRef
  // react-pro-sidebar only fades the suffix out when collapsed, so it would still take up room and
  // push the trigger buttons past the 72px sidebar edge
  const expandIcon = collapsed ? undefined : <i className='bi bi-chevron-right sidebar-popover-expand-icon' />

  // A hover popover has no touch trigger, so the mobile menu renders the entries inline instead.
  if (isMobile) {
    return (
      <div>
        <Divider className='zocks-bg-color-gray-800 divider-sidebar-bottom-fixed' />
        <div className='d-flex flex-column zocks-gap-2.5 p-3'>
          <div className='d-flex flex-column gap-1'>
            <AdvancedModeSwitch />
            {isEnterpriseDashboardEnabled ? <EnterpriseDashboardMenu menuItemStyles={menuItemStyles} /> : null}
            <Menu menuItemStyles={menuItemStyles} className='zocks-menu'>
              <MenuItem
                icon={<i className='bi bi-grid' />}
                component={<NavLink to='/account/integration-settings' />}
                onClick={() => handleMenuItemClick('integrationSettings')}
              >
                <div className='button-small'>Integrations</div>
              </MenuItem>
              {SETTINGS_ENTRIES.map((entry) => (
                <ScopeGate key={entry.key} scope={entry.scope}>
                  <MenuItem onClick={() => handleMenuItemClick(entry.key)} icon={<i className={`bi ${entry.icon}`} />} component={<NavLink to={entry.to} />}>
                    <div className='button-small'>{entry.label}</div>
                  </MenuItem>
                </ScopeGate>
              ))}
              <MenuItem
                onClick={() => handleMenuItemClick('profileSettings')}
                icon={<i className='bi bi-person' />}
                component={<NavLink to='/account/profile-settings' />}
              >
                <div className='button-small'>Profile</div>
              </MenuItem>
              <AcademyEntry variant='menu' loading={loading} onClick={handleAcademyClick} />
              <MenuItem
                icon={<i className='bi bi-question-circle' />}
                component={<a className='ps-menu-button' href={HELP_CENTER_URL} target='_blank' rel='noopener noreferrer' />}
                onClick={() => handleMenuItemClick('helpCenter')}
              >
                <div className='button-small'>Help center</div>
              </MenuItem>
            </Menu>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <Divider className='zocks-bg-color-gray-800 divider-sidebar-bottom-fixed' />
      <div className='d-flex flex-column zocks-gap-2.5 p-3'>
        <div className='d-flex flex-column gap-1'>
          {/* The menu items shrink to their icons on their own, but the switch card has no collapsed form */}
          {collapsed ? null : <AdvancedModeSwitch />}
          {isEnterpriseDashboardEnabled ? <EnterpriseDashboardMenu menuItemStyles={menuItemStyles} /> : null}
          <Menu menuItemStyles={menuItemStyles} className='zocks-menu'>
            <MenuItem
              icon={<i className='bi bi-grid' />}
              component={<NavLink to='/account/integration-settings' />}
              onClick={() => handleMenuItemClick('integrationSettings')}
            >
              <div className='button-small'>Integrations</div>
            </MenuItem>
          </Menu>
          <div ref={settingsRef} {...triggerHandlers('settings')}>
            <Menu menuItemStyles={menuItemStyles} className='zocks-menu'>
              <MenuItem
                icon={<i className='bi bi-gear' />}
                suffix={expandIcon}
                component={
                  <button
                    type='button'
                    className={classNames('w-100 sidebar-trigger-button text-start', { active: isSettingsChildActive })}
                    aria-haspopup='true'
                    aria-expanded={popover.activeItem === 'settings'}
                    aria-controls={popover.mounted && popover.lastItem === 'settings' ? POPOVER_ID : undefined}
                  />
                }
                onClick={onTriggerClick('settings')}
              >
                <div className='button-small'>Settings</div>
              </MenuItem>
            </Menu>
          </div>
          <Menu menuItemStyles={menuItemStyles} className='zocks-menu'>
            <MenuItem
              icon={<i className='bi bi-person' />}
              component={<NavLink to='/account/profile-settings' />}
              onClick={() => handleMenuItemClick('profileSettings')}
            >
              <div className='button-small'>Profile</div>
            </MenuItem>
          </Menu>
          <div ref={helpRef} {...triggerHandlers('help')}>
            <Menu menuItemStyles={menuItemStyles} className='zocks-menu'>
              <MenuItem
                // The popover can be closed while the academy login is still running, so the trigger
                // mirrors its progress
                icon={
                  loading ? <span className='spinner-border spinner-border-sm' role='status' aria-label='Loading' /> : <i className='bi bi-question-circle' />
                }
                suffix={expandIcon}
                component={
                  <button
                    type='button'
                    className='w-100 sidebar-trigger-button text-start'
                    aria-haspopup='true'
                    aria-expanded={popover.activeItem === 'help'}
                    aria-controls={popover.mounted && popover.lastItem === 'help' ? POPOVER_ID : undefined}
                  />
                }
                onClick={onTriggerClick('help')}
              >
                <div className='button-small'>Help &amp; support</div>
              </MenuItem>
            </Menu>
          </div>
        </div>
      </div>
      <Overlay
        target={targetRef}
        show={popover.mounted}
        placement='right-end'
        flip
        transition={false}
        popperConfig={{ modifiers: [{ name: 'flip', options: { fallbackPlacements: ['top-end', 'bottom-end'] } }] }}
      >
        {(overlayProps) => (
          <Popover
            id={POPOVER_ID}
            {...overlayProps}
            className={classNames('sidebar-popover', {
              'sidebar-popover--repositioning': popover.repositioning,
              'sidebar-popover--exiting': popover.exiting,
            })}
          >
            <Popover.Body
              ref={popoverBodyRef}
              className='p-2'
              onMouseEnter={() => popover.open(popover.activeItem ?? popover.lastItem)}
              onMouseLeave={onMouseLeave}
              onKeyDown={onKeyDown}
              onBlur={onBlur}
              onClickCapture={onPopoverItemClick}
            >
              {popover.lastItem === 'settings' ? (
                <div key='settings' className='d-flex flex-column gap-1'>
                  {SETTINGS_ENTRIES.map((entry) => (
                    <ScopeGate key={entry.key} scope={entry.scope}>
                      <NavLink to={entry.to} className={popoverLinkClass} onClick={() => handleMenuItemClick(entry.key)}>
                        <i className={`bi ${entry.icon}`} />
                        <span className='button-small'>{entry.label}</span>
                      </NavLink>
                    </ScopeGate>
                  ))}
                </div>
              ) : (
                <div key='help' className='d-flex flex-column gap-1'>
                  <AcademyEntry variant='popover' loading={loading} onClick={handleAcademyClick} />
                  <a
                    href={HELP_CENTER_URL}
                    target='_blank'
                    rel='noopener noreferrer'
                    className={POPOVER_ENTRY_CLASS}
                    onClick={() => handleMenuItemClick('helpCenter')}
                  >
                    <i className='bi bi-question-circle' />
                    <span className='button-small'>Help center</span>
                  </a>
                </div>
              )}
            </Popover.Body>
          </Popover>
        )}
      </Overlay>
    </div>
  )
}
